import {
  aDosDecimales,
  dividirDinero,
  multiplicarDinero,
  sumarDinero,
} from './dinero';

/** Códigos SUNAT en archivo .rem (declaración PLAME). */
export const CODIGO_PLAME_HE_CONSU_60 = '1001';
export const CODIGO_PLAME_HE_CONSU_100 = '1002';
export const CODIGO_AFECTACION_TRIBUTOS_HE = '0105';

/** Códigos internos del sistema (boleta/planilla); se traducen al declarar PLAME. */
export const CODIGO_INTERNO_HE_CONSU_60 = 'HE60';
export const CODIGO_INTERNO_HE_CONSU_100 = 'HE100';
export const ORIGEN_INGRESO_HE_CONSU = 'horas_extras_construccion_civil';

export const DESCRIPCION_HE_CONSU_60 = 'HORAS EXTRAS 60% (CONSTRUCCIÓN CIVIL)';
export const DESCRIPCION_HE_CONSU_100 = 'HORAS EXTRAS 100% (CONSTRUCCIÓN CIVIL)';

const CLAVE_A_CODIGO_INTERNO = {
  horas_extras_60: CODIGO_INTERNO_HE_CONSU_60,
  horas_extras_100: CODIGO_INTERNO_HE_CONSU_100,
  horas_extras_simple: CODIGO_INTERNO_HE_CONSU_60,
};

export function codigoInternoHorasExtraConsuPorClave(claveConstruccion) {
  return CLAVE_A_CODIGO_INTERNO[claveConstruccion] || null;
}

export function esCodigoInternoHorasExtraConsu(codigo) {
  const c = String(codigo || '').toUpperCase();
  return c === CODIGO_INTERNO_HE_CONSU_60 || c === CODIGO_INTERNO_HE_CONSU_100;
}

function codigoPlameRemPorClaveConsu(clave, plamePorClave) {
  if (!clave) return null;
  const row = plamePorClave?.[clave];
  const codigo = row?.codigo_plame ?? row?.codigo;
  if (codigo) return String(codigo);
  if (clave === 'horas_extras_60' || clave === 'horas_extras_simple') return CODIGO_PLAME_HE_CONSU_60;
  if (clave === 'horas_extras_100') return CODIGO_PLAME_HE_CONSU_100;
  return null;
}

/** Traduce concepto (código interno HE60/HE100) al código del archivo .rem. */
export function codigoPlameParaDeclaracionRem(concepto, opciones = {}) {
  const plamePorClave = opciones?.plamePorClave || null;
  const codigo = String(concepto?.codigoPlame || concepto?.codigo_plame || concepto?.codigo || '').toUpperCase();

  if (codigo === CODIGO_INTERNO_HE_CONSU_60) {
    const clave = concepto?.claveConstruccion || 'horas_extras_60';
    return codigoPlameRemPorClaveConsu(clave, plamePorClave) || CODIGO_PLAME_HE_CONSU_60;
  }
  if (codigo === CODIGO_INTERNO_HE_CONSU_100) {
    return codigoPlameRemPorClaveConsu('horas_extras_100', plamePorClave) || CODIGO_PLAME_HE_CONSU_100;
  }

  const decl = concepto?.codigo_plame_declaracion ?? concepto?.codigoPlameDeclaracion;
  if (decl) return String(decl);

  const clave = concepto?.claveConstruccion;
  if (clave && CLAVE_A_CODIGO_INTERNO[clave]) {
    const mapped = codigoPlameRemPorClaveConsu(clave, plamePorClave);
    if (mapped) return mapped;
  }

  return String(concepto?.codigoPlame || concepto?.codigo_plame || concepto?.codigo || '');
}

export function valorHoraOrdinaria(jornal, horasJornada = 8) {
  const j = Number(jornal) || 0;
  const h = Number(horasJornada) > 0 ? Number(horasJornada) : 8;
  return dividirDinero(j, h);
}

export function esDiaEspecialHorasExtras({ fecha, tipoEvento, tareo } = {}) {
  const f = String(fecha || tareo?.fecha || '').slice(0, 10);
  if (f && new Date(`${f}T12:00:00`).getDay() === 0) return true;
  const evento = String(tipoEvento || tareo?.tipo_evento || tareo?.evento || '').toLowerCase();
  if (evento.includes('feriado_laborado') || evento === 'feriado laborado') return true;
  if (evento.includes('descanso_laborado') || evento === 'descanso laborado') return true;
  return false;
}

/**
 * Clasifica horas extra de una sola jornada (no acumula entre días).
 */
export function clasificarHorasExtrasPorJornada({ horasExtra = 0, diaEspecial = false } = {}) {
  const total = Math.max(0, Number(horasExtra) || 0);
  if (diaEspecial) {
    return { he60: 0, he100: total };
  }
  const he60 = Math.min(2, total);
  const he100 = Math.max(0, total - 2);
  return { he60, he100 };
}

export function montosHorasExtras({
  jornal,
  horasJornada = 8,
  he60 = 0,
  he100 = 0,
  factor60 = 1.6,
  factor100 = 2,
} = {}) {
  const valorHora = valorHoraOrdinaria(jornal, horasJornada);
  const monto60 = aDosDecimales(multiplicarDinero(multiplicarDinero(valorHora, he60), factor60));
  const monto100 = aDosDecimales(multiplicarDinero(multiplicarDinero(valorHora, he100), factor100));
  return {
    monto60,
    monto100,
    total: aDosDecimales(sumarDinero(monto60, monto100)),
  };
}

export function resolverSubtipoAutorizacionConsu(subtipo) {
  const s = String(subtipo || '').toLowerCase();
  if (s === 'horas-extra-60' || s.includes('extra-60')) return '60';
  if (s === 'horas-extra-100' || s.includes('extra-100')) return '100';
  return null;
}

/** Mezcla horas de autorizaciones aprobadas en tareos por fecha (CONSU). */
export function enriquecerTareosConHorasExtrasConsu(tareos = [], autorizaciones = []) {
  const advertencias = [];
  const porFecha = {};
  (tareos || []).forEach((t) => {
    const fecha = String(t.fecha || t.fecha_tareo || '').slice(0, 10);
    if (!fecha) return;
    porFecha[fecha] = { ...t, fecha };
  });

  (autorizaciones || []).forEach((auth) => {
    if (String(auth.tipo_autorizacion || '') !== 'horas-extra') return;
    if (String(auth.estado || '').toLowerCase() !== 'aprobado') return;
    const tramo = resolverSubtipoAutorizacionConsu(auth.sub_tipo || auth.subtipo);
    if (!tramo) return;
    const fecha = String(auth.fecha_inicio || auth.fecha || '').slice(0, 10);
    if (!fecha) return;
    const horas = Number(auth.horas || 0);
    if (horas <= 0) return;

    const add60 = tramo === '60' ? horas : 0;
    const add100 = tramo === '100' ? horas : 0;
    const prev = porFecha[fecha] || { fecha };
    const prev60 = Number(prev.horas_extra_60 ?? prev.he_60 ?? 0);
    const prev100 = Number(prev.horas_extra_100 ?? prev.he_100 ?? 0);
    porFecha[fecha] = {
      ...prev,
      horas_extra_60: prev60 + add60,
      horas_extra_100: prev100 + add100,
      he_60: prev60 + add60,
      he_100: prev100 + add100,
    };
  });

  const fechasOrdenadas = Object.keys(porFecha).sort();
  const tareosOut = fechasOrdenadas.map((f) => porFecha[f]);
  const extras = (tareos || []).filter((t) => {
    const f = String(t.fecha || t.fecha_tareo || '').slice(0, 10);
    return f && !porFecha[f];
  });
  return { tareos: [...tareosOut, ...extras], advertencias };
}
